import React, { useState, useContext, useMemo } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';

import { EditableFieldsContext } from '../context';

import { FieldDisplay } from './utils';

import { FormikTextField } from 'components/fields/FormikTextField';
import { Btn } from 'components/btn/Btn';
import { sameEmailValidator } from 'common/modules/form-validation/validators';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { Family } from 'common/api/crm/models/Family';
import { updateUserThunk } from 'common/features/store/duck/home/duck';
import { StatusCode } from 'common/api/config';

export const EmailFieldDisplay = ({ isEditing, id }: { isEditing: boolean; id: string }) => {
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const shouldDisable = !profile?.firstName;

    return (
        <FieldDisplay id={id} isEditing={isEditing} label="Email">
            {isEditing ? (
                <p className="text-sm text-brand-dark">
                    This email update will become your new username for signing in.
                </p>
            ) : (
                profile?.email
            )}
            {shouldDisable && <Skeleton className="h-10" />}
        </FieldDisplay>
    );
};

interface EmailFieldValues {
    email: string;
}

export const EmailFieldEditComponent = () => {
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const initialValues = { email: profile?.email ?? '' };

    const { setFieldToEdit } = useContext(EditableFieldsContext);
    const [errorExists, setErrorExists] = useState(false);
    const dispatch = useDispatch<any>();

    const emailValidator_ = useMemo(() => {
        return sameEmailValidator(profile?.email || '');
    }, [profile?.email]);

    const onSubmit = async (
        values: EmailFieldValues,
        formikContext: FormikHelpers<{ email: string }>
    ) => {
        const payload = {
            ...profile,
            ...values,
        };

        try {
            const response = await updateUserThunk(payload as Family)(dispatch);
            if (response?.response?.status === StatusCode.Conflict) {
                formikContext.setFieldError(
                    'email',
                    'Sorry that user name / email address is not available.'
                );
            } else {
                setFieldToEdit('');
            }
        } catch (e) {
            // renders a common message declaring that it's unable to update information at this point
            setErrorExists(true);
        }
    };

    return (
        <div className="mt-5">
            <Formik initialValues={initialValues} onSubmit={onSubmit} validateOnMount>
                {(helpers) => {
                    return (
                        <Form>
                            <div className="flex sm:flex-row">
                                <div className="mb-5 sm:mr-5 w-full max-w-xs">
                                    <FormikTextField
                                        name="email"
                                        label="Email"
                                        validate={emailValidator_}
                                    />
                                </div>
                            </div>
                            {errorExists && (
                                <p className="text-sm mb-3 text-error">
                                    Unable to update your information at this time
                                </p>
                            )}
                            <Btn disabled={!helpers.isValid} type="submit">
                                Save
                            </Btn>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};
